import { RegisterOptions, useFormContext } from "react-hook-form";
import { Button, CheckBox, Font } from "@moneyforward/mfx_biz_react_components";
import {
  KanaNameFormField,
  NameFormField,
  PasswordFormField,
  MiddleNameFormField,
  KanaMiddleNameFormField,
  UsernameLabel,
  BizsolLoginIdLabel,
  ChangeBizsolStateButton,
  AuthorityBlock,
  AuthorityBlockUneditable,
  EmailBlock,
} from "@/components/molecules";
import React, { ChangeEvent, useState, useEffect } from "react";
import { useIsSpMode } from "@/hooks";
import { css } from "@emotion/react";
import { color } from "@/styles/theme";
import { Authority, BizsolState } from "@/generated/graphql";
import {
  CancelButton,
  DeleteConfirmModal,
  BizsolReconnectConfirmModal,
} from "@/components/atoms";
import { EditUserFormValues } from "@/components/organisms";
import { isConnectedToBizsol } from "@/models";
import config from "@/libs/config";
import { BizsolDisconnectModal } from "@/components/organisms/BizsolDisconnectModal";
import { BizsolDisconnectModalState } from "@/store/bizsolDisconnectModalState";

type Props = {
  username: string;
  authority: Authority;
  email: string;
  sub: string;
  waitingForChangeToAdmin: boolean;
  bizsolState: BizsolState;
  bizsolLoginID: string;
  isOwnUserProfile: boolean;
  onSubmit: (formValues: EditUserFormValues) => void;
  onClickChangeToMember: () => void;
  onClickChangeToAdmin: () => void;
  onClickDelete: () => void;
  onClickChangeEmailAddress: () => void;
  onClickResendEmail: () => void;
  onClickChangeToWaiting: () => void;
  onClickCancelWaiting: () => void;
  onClickReconnect: () => void;
  onClickDisconnect: (disconnectState: BizsolDisconnectModalState) => void;
  submitting: boolean;
  passwordRegisterOptions?: RegisterOptions;
  onInputPassword?: (event: ChangeEvent<HTMLInputElement>) => void;
  meIsAdminUsersAtMikatanoID: boolean;
  userIsAdminUsersAtMikatanoID: boolean;
};

export const EditUserFormBizsol = (props: Props) => {
  const isSpMode = useIsSpMode();
  const { getValues, handleSubmit } = useFormContext();
  const [deleteConfirmModalActive, setDeleteConfirmModalActive] =
    useState<boolean>(false);
  const [reconnectConfirmModalActive, setReconnectConfirmModalActive] =
    useState<boolean>(false);
  const [disconnectModalActive, setDisconnectModalActive] =
    useState<boolean>(false);
  const [usePasswordMeter, setUsePasswordMeter] = useState<boolean>(true);

  const onClickButtonInModal = () => {
    setDeleteConfirmModalActive(false);
  };

  const onClickButtonInReconnectModal = () => {
    setReconnectConfirmModalActive(false);
  };

  const onClickCloseButtonInDisconnectModal = () => {
    setDisconnectModalActive(false);
  };

  const connectedToBizsol = isConnectedToBizsol(props.bizsolState);
  const connectedOrWaitingBizsol =
    connectedToBizsol || props.bizsolState === BizsolState.Waiting;

  // 銀行に応じて処理を分ける.
  // 四国銀行
  // - bizsol連携ボタンを表示する
  // - bizsol連携済みの場合emailは編集不可
  // - bizsol連携済みの場合パスワードは編集不可
  // - bizsol連携済みの場合再連携ボタンを表示する
  // 京都信金
  // - bizsol連携ボタンは表示しない
  // - bizsol連携済みの場合もemailは編集可能
  // - bizsol連携済みの場合もパスワードは編集可能
  // - bizsol連携済み＆ログインユーザー自身の編集画面の場合は、再連携ボタンを表示する

  // shikokubank
  // - show bizsol button
  // - bizsol linked - email not editable
  // - bizsol linked - password not editable
  // - bizsol linked - show reconnect button

  // kyoshin
  // - do not show bizsol button
  // - bizsol linked - email editable
  // - bizsol linked - password editable
  // - bizsol linked & viewing own profile - show reconnect button

  const displayPasswordField = config.IS_KYOSHIN || !connectedToBizsol;
  const isAuthorityEditable = !connectedOrWaitingBizsol; // bizsol 連携済み・連携待ちユーザーは権限変更不可
  const isEmailEditable = config.IS_KYOSHIN || !connectedOrWaitingBizsol;
  // 連携状態変更ボタン(連携・再連携)を表示するかどうか
  const displayChangeBizsolStateButton =
    config.IS_SHIKOKUBANK ||
    (config.IS_KYOSHIN && connectedToBizsol && props.isOwnUserProfile);
  const displaySupportSiteFootnote = config.IS_SHIKOKUBANK && connectedToBizsol;

  const onClickReconnect = config.IS_SHIKOKUBANK
    ? () => {
        setDisconnectModalActive(true);
      }
    : () => {
        setReconnectConfirmModalActive(true);
      };

  useEffect(() => {
    setUsePasswordMeter(getValues(["password"]).shift()?.length !== 0);
  }, [getValues(["password"])]);

  const [enableDelete, setEnableDelete] = useState<boolean>(false);
  const [displayErrorTxt, setDisplayErrorTxt] = useState<boolean>();
  const deleteModalBody = (
    <div css={deleteModalWrapper}>
      <div>
        <Font>利用者を削除しますか？</Font>
        <br />
        <Font>※ 削除前に必ずご確認ください</Font>
        <Font>
          Mikatano
          IDから利用者削除を行う際は、先にご利用中サービスの利用者一覧から該当の利用者の状態を利用停止に変更してください。
        </Font>
      </div>
      <div css={deleteModalCheckbox}>
        <CheckBox
          checked={enableDelete}
          onChange={() => {
            setEnableDelete(!enableDelete);
            if (displayErrorTxt !== undefined) {
              setDisplayErrorTxt(enableDelete);
            }
          }}
          bgSize="sm"
        />
        <Font>この動作は取り消すことができません</Font>
      </div>
      {displayErrorTxt && (
        <div>
          <Font variant="s" css={displayErrorTxtColor}>
            チェックを入れてください。
          </Font>
        </div>
      )}
    </div>
  );

  return (
    <div css={container}>
      <div css={!isSpMode ? box : spModeBox}>
        <form
          onSubmit={handleSubmit(props.onSubmit)}
          css={form}
          autoComplete="off"
        >
          <div css={!isSpMode ? inputList : spModeInputList}>
            <div css={listItem}>
              <NameFormField
                nameLastName="lastName"
                nameFirstName="firstName"
              />
            </div>
            <div css={listItem}>
              <KanaNameFormField
                nameLastName="lastNameKana"
                nameFirstName="firstNameKana"
              />
            </div>
            <div css={listItem}>
              <MiddleNameFormField name="middleName" />
            </div>
            <div css={listItem}>
              <KanaMiddleNameFormField name="middleNameKana" />
            </div>
            <EmailBlock
              email={props.email}
              onClick={props.onClickChangeEmailAddress}
              loading={props.submitting}
              editable={isEmailEditable}
            />
            <div
              css={{ border: "1px solid #DADEDF", margin: "10px 0 20px 0" }}
            />
            <div css={listItem}>
              <UsernameLabel username={props.username} />
            </div>
            <div css={listItem}>
              {displayPasswordField && (
                <PasswordFormField
                  name="password"
                  usePasswordMeter={usePasswordMeter}
                  registerOptions={props.passwordRegisterOptions}
                  onInput={props.onInputPassword}
                  labelText={
                    props.isOwnUserProfile
                      ? ""
                      : "パスワードを変更した場合は、7日以内にログインしてください"
                  }
                />
              )}
            </div>
            <div
              css={{ border: "1px solid #DADEDF", margin: "10px 0 20px 0" }}
            />
            {isAuthorityEditable ? (
              <AuthorityBlock
                authority={props.authority}
                waitingForChangeToAdmin={props.waitingForChangeToAdmin}
                onClickChangeToMember={props.onClickChangeToMember}
                onClickChangeToAdmin={props.onClickChangeToAdmin}
                onClickResendEmail={props.onClickResendEmail}
                loading={props.submitting}
                meIsAdminUsersAtMikatanoID={props.meIsAdminUsersAtMikatanoID}
                userIsAdminUsersAtMikatanoID={
                  props.userIsAdminUsersAtMikatanoID
                }
              />
            ) : (
              <AuthorityBlockUneditable authority={props.authority} />
            )}
            <div>
              {displayChangeBizsolStateButton && (
                <ChangeBizsolStateButton
                  state={props.bizsolState}
                  onClickChangeToWaiting={props.onClickChangeToWaiting}
                  onClickCancelWaiting={props.onClickCancelWaiting}
                  onClickReconnect={onClickReconnect}
                  loading={props.submitting}
                />
              )}
            </div>
            <div>
              <BizsolLoginIdLabel id={props.bizsolLoginID} />
            </div>
            {displaySupportSiteFootnote && (
              <div css={{ margin: "16px 0" }}>
                <Font variant={"s"}>
                  ※電子証明書の再発行などについての不明点は
                  <a
                    href={"https://support.id.x.moneyforward.com/hc/ja"}
                    target="_blank"
                    css={link}
                  >
                    Mikatano ID サポートサイト
                  </a>
                  からご確認ください
                </Font>
              </div>
            )}
          </div>

          <div css={buttonWrapper}>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setDeleteConfirmModalActive(true);
              }}
              disabled={props.submitting}
            >
              削除
            </Button>
            <CancelButton />
            <Button
              type="submit"
              variant="primary"
              css={buttonMargin}
              disabled={props.submitting}
            >
              保存
            </Button>
          </div>
        </form>
      </div>

      <DeleteConfirmModal
        active={deleteConfirmModalActive}
        title="利用者の削除"
        body={deleteModalBody}
        onClickCancel={onClickButtonInModal}
        onClickDelete={() => {
          if (!enableDelete) {
            setDisplayErrorTxt(true);
            return;
          }
          onClickButtonInModal();
          props.onClickDelete();
        }}
      />
      <BizsolReconnectConfirmModal
        active={reconnectConfirmModalActive}
        title={`${config.BIZSOL_DISPLAY_NAME}の再連携`}
        onClickCancel={onClickButtonInReconnectModal}
        onClickReconnect={() => {
          onClickButtonInReconnectModal();
          props.onClickReconnect();
        }}
      />
      <BizsolDisconnectModal
        active={disconnectModalActive}
        title={`${config.BIZSOL_DISPLAY_NAME}IDとの連携を解除`}
        sub={props.sub}
        username={props.username}
        onClickCancel={onClickCloseButtonInDisconnectModal}
        onClickDisconnect={props.onClickDisconnect}
      />
    </div>
  );
};

const container = css({
  display: "flex",
  justifyContent: "center",
});

const box = css({
  width: "640px",
  minHeight: "245px",
  padding: "40px 32px",
  border: `1px solid ${color.iron}`,
  borderRadius: "4px",
});

const spModeBox = css({
  width: "640px",
  minHeight: "245px",
  padding: "15px 17px",
  border: `1px solid ${color.iron}`,
  borderRadius: "4px",
});

const form = css({
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
});

const inputList = css({
  flex: 1,
});

const spModeInputList = css({
  flex: 1,
});

const listItem = css({
  marginBottom: "15px",
});

const buttonWrapper = css({
  display: "flex",
  justifyContent: "flex-end",
  marginTop: "8px",
});
const buttonMargin = css({
  marginLeft: "16px",
});

const deleteModalWrapper = css({
  marginTop: "16px",
});

const deleteModalCheckbox = css({
  display: "flex",
  alignItems: "center",
  marginTop: "26px",
});

const displayErrorTxtColor = css({
  color: color.attention,
});

const link = css({
  color: color.link,
  ":hover": {
    textDecoration: "underline",
    opacity: 0.7,
    cursor: "pointer",
  },
});
